import type { ComponentProps } from "react"
import { memo, useState } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { AnimatePresence, motion } from "motion/react"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	IconButton,
	ScrollArea,
	ScrollViewport,
	Shortcut,
	Tooltip,
} from "@productlane/ui"
import { AiAssistantIcon } from "@productlane/ui/src/icons/project/ai-assistant-icon"

import { useShortcut } from "@/lib"
import { useCopilotState } from "@/lib/states/copilot-state"
import { useRole } from "@/lib/useRole"
import { useThreadSidebarState } from "@/screens/app/sidebar"
import { useThreadContext } from "../../thread-context"
import { SidebarAiChat } from "./ai-chat"
import { COPILOT_SURFACE_CLASSES } from "./ai-chat/copilot-composer-chrome"
import { CustomSidebarSections } from "./custom-sidebar-sections"
import { CustomerProperties } from "./customer-properties"
import { LinkedEntities } from "./linked-entities"
import { RecentThreads } from "./recent-threads"
import { SidebarGroup, SidebarGroupLabel } from "./shared/sidebar-group"
import { SidebarTopSection } from "./sidebar-top-section"

type Props = ComponentProps<"div">

export const RightBar = memo(function RightBar({
	className,
	asDialog = false,
	...props
}: Props & { asDialog?: boolean }) {
	const threadSidebarState = useThreadSidebarState()

	return (
		<>
			{asDialog ? (
				<AnimatePresence>
					{threadSidebarState.isOpen && (
						<DialogPrimitive.Root
							open
							onOpenChange={(open) => {
								if (!open) {
									threadSidebarState.close()
								}
							}}
						>
							<DialogPrimitive.Portal forceMount>
								<DialogPrimitive.Overlay forceMount asChild>
									<motion.div
										className="fixed inset-0 z-20 bg-black/50"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										transition={{ duration: 0.1 }}
										exit={{ opacity: 0 }}
									/>
								</DialogPrimitive.Overlay>
								<DialogPrimitive.Content
									forceMount
									asChild
									onPointerDownOutside={(e) => {
										const target = e.target as HTMLElement | null
										if (target?.closest("[data-sidebar-toggle]")) {
											e.preventDefault()
										}
									}}
								>
									<motion.div
										initial={{ translateX: "100%" }}
										animate={{ translateX: 0 }}
										transition={{
											duration: 0.2,
											ease: "easeInOut",
										}}
										exit={{ translateX: "100%" }}
										className="fixed bottom-0 right-0 top-0 z-50 flex w-full flex-col bg-gray-50 sm:w-[24rem] dark:bg-gray-850"
									>
										<DialogPrimitive.Close asChild>
											<IconButton
												Icon={X}
												variant="ghost"
												aria-label="Close sidebar"
												className="absolute right-3 top-3 z-20 sm:hidden"
											/>
										</DialogPrimitive.Close>
										<RightBarContent
											className={cn(
												"h-full w-full min-w-0 max-w-none",
												className,
											)}
											inlineAiChat
											{...props}
										/>
									</motion.div>
								</DialogPrimitive.Content>
							</DialogPrimitive.Portal>
						</DialogPrimitive.Root>
					)}
				</AnimatePresence>
			) : (
				<RightBarContent className={className} {...props} />
			)}
		</>
	)
})

const AiAssistantButton = ({
	active,
	onClick,
}: {
	active: boolean
	onClick: () => void
}) => (
	<Tooltip
		label={
			<span className="inline-flex items-center gap-2">
				Show AI Copilot
				<Shortcut>
					{ShortcutSymbols.CMD}
					{ShortcutSymbols.J}
				</Shortcut>
			</span>
		}
		side="bottom"
	>
		<button
			onClick={onClick}
			aria-pressed={active}
			className={cn(
				"relative flex h-8 w-8 items-center justify-center rounded-full",
				active
					? "bg-gray-200 text-primary dark:bg-gray-750"
					: "text-tertiary hover:bg-gray-200 hover:text-primary dark:hover:bg-gray-750",
			)}
		>
			<AiAssistantIcon />
		</button>
	</Tooltip>
)

export const RightBarContent = ({
	className,
	inlineAiChat = false,
	...props
}: Props & { inlineAiChat?: boolean }) => {
	const { activeTab, setActiveTab } = useThreadSidebarState()
	const [isScrolled, setIsScrolled] = useState(false)
	const { thread } = useThreadContext()
	const { role } = useRole()
	const canSeeAiChat = role !== "VIEWER"
	const showInlineAiChat =
		inlineAiChat && canSeeAiChat && activeTab === "ai-chat"

	// The copilot's composer takes focus only on an explicit open, and the
	// request carries this thread so it can never land on the next one.
	const requestCopilotFocus = useCopilotState((s) => s.requestFocus)
	const openCopilot = () => {
		setActiveTab("ai-chat")
		requestCopilotFocus({ kind: "thread", threadId: thread.id })
	}

	useShortcut("d", () => setActiveTab("details"))
	useShortcut("y", () => {
		if (canSeeAiChat) openCopilot()
	})
	// cmd+j must toggle the copilot even while a text field / editor is focused
	// or text is selected, unlike the other single-key shortcuts. Enabling form
	// tags + contenteditable here (and only here) opts this one shortcut out of
	// the typing guard.
	useShortcut(
		"meta+j, ctrl+j",
		() => {
			if (!canSeeAiChat) return
			if (activeTab === "ai-chat") setActiveTab("details")
			else openCopilot()
		},
		{
			preventDefault: true,
			enableOnFormTags: true,
			enableOnContentEditable: true,
		},
	)

	return (
		<div
			data-thread-details
			className={cn(
				"relative flex w-[var(--thread-details-width,22rem)] flex-col overflow-hidden bg-gray-100 dark:bg-gray-850",
				thread.isDeleted && "pointer-events-none opacity-40",
				className,
			)}
			{...props}
		>
			{/* Header actions: AI copilot toggle */}
			<div className="absolute right-12 top-3 z-10 flex items-center gap-1 sm:right-3">
				{canSeeAiChat && (
					<div className="relative">
						<div
							data-testid="ai-copilot-button-scrim"
							className={cn(
								"pointer-events-none absolute -inset-1 rounded-full bg-gray-100/60 backdrop-blur-md transition-opacity duration-150 dark:bg-gray-850/60",
								isScrolled ? "opacity-100" : "opacity-0",
							)}
						/>
						<AiAssistantButton
							active={activeTab === "ai-chat"}
							onClick={() => {
								if (activeTab === "ai-chat") setActiveTab("details")
								else openCopilot()
							}}
						/>
					</div>
				)}
			</div>

			{/* Desktop: always renders the "details" pane; the AI chat is
			rendered as a persistent overlay in the parent layout so it
			survives thread switches without remounting. Mobile (inlineAiChat):
			renders the AI chat in-place when its tab is active. */}
			{showInlineAiChat ? (
				<div className={cn("flex-1 overflow-hidden", COPILOT_SURFACE_CLASSES)}>
					<SidebarAiChat
						scope={{
							kind: "thread",
							threadId: thread.id,
							threadTitle: thread.title ?? undefined,
						}}
					/>
				</div>
			) : (
				<ScrollArea>
					<ScrollViewport
						onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 0)}
					>
						<div className="px-2 pb-20">
							<SidebarTopSection />
							<LinkedEntities />

							{!!thread.company && (
								<SidebarGroup className="cursor-default select-none">
									<SidebarGroupLabel>Customer</SidebarGroupLabel>
									<CustomerProperties company={thread.company} {...props} />
								</SidebarGroup>
							)}

							<RecentThreads />

							<CustomSidebarSections
								target={{ type: "thread", id: thread.id }}
							/>
						</div>
					</ScrollViewport>
				</ScrollArea>
			)}
		</div>
	)
}
